import {createContext, FC, ReactNode, useContext, useEffect, useState} from 'react'
import {useQuery} from 'react-query'
import {getAllData, searchIvrData} from './_requests'
import {ModelQueryResponse, Model} from './_models'
import {initialQueryState} from '../../../../../../_metronic/helpers'
import {useQueryRequest} from './QueryRequestProvider'
import {useParams} from 'react-router-dom'

interface QueryState {
  search?: string
  page?: number
  items_per_page?: number
  sort?: string
  order?: 'asc' | 'desc'
}

const QueryResponseContext = createContext<{
  isLoading: boolean
  data: Array<Model>
  refetch: () => void
  query: string
  pagination: {
    page: number
    last_page: number
    total: number
    items_per_page: number
    to: number
    from: number
  }
}>({
  isLoading: false,
  data: [],
  refetch: () => {},
  query: '',
  pagination: {
    page: 1,
    last_page: 1,
    total: 0,
    items_per_page: 10,
    to: 0,
    from: 0
  }
})

const QueryResponseProvider: FC<{children: ReactNode}> = ({children}) => {
  const {state} = useQueryRequest()
  const [query, setQuery] = useState<string>('')
  const {id: customerCode} = useParams<{id: string}>()

  const fetchData = async (): Promise<ModelQueryResponse> => {
    if (!customerCode) {
      return {data: [], payload: {pagination: {total: 0, last_page: 1, page: 1, items_per_page: 10, to: 0, from: 0}}}
    }
    
    try {
      // Check if there's a search term
      const urlParams = new URLSearchParams(query)
      const searchTerm = urlParams.get('searchTerm')
      
      if (searchTerm && searchTerm.trim()) {
        // Use search API when there's a search term
        return await searchIvrData(customerCode, searchTerm.trim())
      } else {
        // Use regular getAllData when no search term
        return await getAllData(`customerCode=${customerCode}&${query}`)
      }
    } catch (error) {
      console.error('Error fetching IVR data from API:', error)
      // Return empty data instead of mock data
      return {data: [], payload: {pagination: {total: 0, last_page: 1, page: 1, items_per_page: 10, to: 0, from: 0}}}
    }
  }

  const {
    isLoading,
    data = [],
    refetch,
  } = useQuery(
    [`user-has-ivr-${customerCode}-${query}`],
    fetchData,
    {
      refetchOnWindowFocus: false,
      enabled: true, // Always enabled to allow API calls
      retry: 2, // Retry failed requests twice
      retryDelay: 1000, // Wait 1 second between retries
    }
  )

  // Extract data from the response
  const processedData = Array.isArray(data) ? data : (data as any)?.data || []
  const paginationData = (data as any)?.payload?.pagination || {
    page: 1,
    last_page: 1,
    total: processedData.length,
    items_per_page: 10,
    to: processedData.length,
    from: 1
  }
  

  useEffect(() => {
    const params = new URLSearchParams()
    if (state.searchTerm) {
      params.append('searchTerm', state.searchTerm)
    }
    if (state.page) {
      params.append('page', state.page.toString())
    }
    if (state.items_per_page) {
      params.append('items_per_page', state.items_per_page.toString())
    }
    if (state.sort) {
      params.append('sort', state.sort)
    }
    if (state.order) {
      params.append('order', state.order)
    }
    setQuery(params.toString())
  }, [state])

  return (
    <QueryResponseContext.Provider value={{isLoading, data: processedData, refetch, query, pagination: paginationData}}>
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryResponseContext)
const useQueryResponseData = () => {
  const {data} = useQueryResponse()
  return data
}
const useQueryResponseLoading = () => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

const useQueryResponsePagination = () => {
  const {pagination} = useQueryResponse()
  return pagination
}

export {QueryResponseProvider, useQueryResponse, useQueryResponseData, useQueryResponseLoading, useQueryResponsePagination}
